import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useLang } from "../../../shared/i18n/useLang";
import {
  duracion, hora, listarRegistros, marcarSalida, texto as textoDe, textoMotivo,
} from "../services/seguridadService";

/**
 * Quién sigue adentro (con el botón para marcar su salida) y los movimientos
 * recientes. Se refresca solo cada 30 segundos.
 */
export default function GaritaAdentro() {
  const { recargarAdentro } = useOutletContext();
  const L = useLang();
  const [vista, setVista] = useState("adentro");   // adentro | recientes
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [confirmar, setConfirmar] = useState(null); // id que pide confirmación
  const [error, setError] = useState("");
  const [, setTic] = useState(0);                   // para refrescar las duraciones

  const cargar = useCallback(async () => {
    try {
      const d = await listarRegistros(vista === "adentro" ? { adentro: 1 } : {});
      setRegistros(d);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [vista]);

  useEffect(() => {
    setCargando(true);
    cargar();
    const t = setInterval(() => { cargar(); setTic((x) => x + 1); }, 30000);
    return () => clearInterval(t);
  }, [cargar]);

  // Un toque pide confirmación; el segundo marca la salida.
  async function salida(r) {
    if (confirmar !== r.id) {
      setConfirmar(r.id);
      setTimeout(() => setConfirmar((c) => (c === r.id ? null : c)), 4000);
      return;
    }
    setConfirmar(null);
    try {
      await marcarSalida(r.id);
      await cargar();
      recargarAdentro();
    } catch (e) {
      setError(e.message);
    }
  }

  const f = filtro.trim().toLowerCase();
  const lista = registros.filter((r) => !f || [r.nombre, r.identificacion, r.placa, r.filial_nombre, r.persona_visitada]
    .some((x) => (x || "").toLowerCase().includes(f)));

  return (
    <>
      <h1 className="gar-titulo">{L("Movimientos", "Activity")}</h1>

      <div className="gar-switch" role="group" style={{ marginBottom: 12 }}>
        <button type="button" className={vista === "adentro" ? "activo" : ""} onClick={() => setVista("adentro")}>
          <i className="fa-solid fa-people-arrows" /> {L("Adentro ahora", "Inside now")}
        </button>
        <button type="button" className={vista === "recientes" ? "activo" : ""} onClick={() => setVista("recientes")}>
          <i className="fa-solid fa-list" /> {L("Recientes", "Recent")}
        </button>
      </div>

      <div className="gar-buscar" style={{ marginBottom: 12 }}>
        <i className="fa-solid fa-filter" />
        <input className="gar-input" type="search"
          placeholder={L("Filtrar por nombre, casa o placa", "Filter by name, unit or plate")}
          value={filtro} onChange={(e) => setFiltro(e.target.value)} />
      </div>

      {error && <div className="gar-aviso error" style={{ marginBottom: 12 }}><i className="fa-solid fa-triangle-exclamation" />{error}</div>}

      {!cargando && lista.length === 0 && (
        <div className="gar-card gar-vacio">
          {vista === "adentro"
            ? L("No hay nadie adentro registrado.", "Nobody registered is inside.")
            : L("No hay movimientos recientes.", "No recent activity.")}
        </div>
      )}

      {lista.map((r) => (
        <div key={r.id} className="gar-card">
          <div className="gar-fila" style={{ alignItems: "flex-start", flexWrap: "nowrap" }}>
            <div className="gar-avatar">
              <i className={`fa-solid ${r.tipo === "VEHICULAR" ? "fa-car-side" : "fa-person-walking"}`} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: 16 }}>{r.nombre}</strong>
              <div className="gar-fila" style={{ gap: 6, marginTop: 4 }}>
                <span className="gar-tag marca">{r.filial_nombre}</span>
                {r.categoria !== "VISITA" && <span className="gar-tag azul">{textoDe(r.categoria)}</span>}
                {r.categoria === "VISITA" && <span className="gar-tag">{textoMotivo(r.motivo)}</span>}
                {r.placa && <span className="gar-placa">{r.placa}</span>}
              </div>
              <div style={{ color: "var(--gar-suave)", fontSize: 13, marginTop: 6 }}>
                {r.persona_visitada && <>{L("Visita a", "Visiting")} {r.persona_visitada} · </>}
                {L("Entró", "In")} {hora(r.entrada)}
                {r.salida
                  ? <> · {L("Salió", "Out")} {hora(r.salida)} ({duracion(r.entrada, r.salida)})</>
                  : <> · {L("hace", "")} {duracion(r.entrada)}{L("", " ago")}</>}
              </div>
              {r.notas && <div style={{ fontSize: 13, marginTop: 4 }}><i className="fa-solid fa-note-sticky" /> {r.notas}</div>}
            </div>
          </div>
          {!r.salida && (
            <button type="button" style={{ marginTop: 10 }}
              className={`gar-btn gar-btn-bloque ${confirmar === r.id ? "gar-btn-peligro" : "gar-btn-sec"}`}
              onClick={() => salida(r)}>
              <i className="fa-solid fa-right-from-bracket" />
              {confirmar === r.id
                ? L("Tocá otra vez para confirmar la salida", "Tap again to confirm the exit")
                : L("Marcar salida", "Mark exit")}
            </button>
          )}
        </div>
      ))}
    </>
  );
}
